/// <amd-module name="VSS/Platform/Feature" />
import { IVssPageContext, IVssService, Services, VssService } from "./Context";
import { IVssContributionService } from "./Contribution";

/**
 * Service for obtaining feature states.
 */
export interface IVssFeatureService extends IVssService {
    /**
     * Gets the enabled state of a feature that was sent to the client using IClientFeatureProviderService (within a data provider or request handler).
     *
     * @param featureId The full contribution id of the feature
     * @param defaultValue If specified, the value to return if the feature state was not seeded through shared data.
     */
    isFeatureEnabled(featureId: string, defaultValue?: boolean): boolean;

    /**
     * Gets the enabled state of a feature flag that was sent to the client using IClientFeatureProviderService (within a data provider or request handler).
     *
     * @param featureFlagName The name of the feature flag
     * @param defaultValue If specified, the value to return if the feature flag was not seeded through shared data.
     */
    isFeatureFlagEnabled(featureFlagName: string, defaultValue?: boolean): boolean;
}

interface IFeatureMap {
    [featureName: string]: boolean;
}

class VssFeatureService extends VssService implements IVssFeatureService {
    public isFeatureEnabled(featureId: string, defaultValue?: boolean): boolean {
        const result = this.getSharedDataState("_features", featureId, defaultValue);
        if (result === undefined) {
            const message = `Could not find feature state for feature '${featureId}'. Ensure that the feature is included in shared data.`;
            if ($DEBUG) {
                throw new Error(message);
            } else {
                console.warn(message);
            }

            return false;
        }

        return result;
    }

    public isFeatureFlagEnabled(featureFlagName: string, defaultValue?: boolean): boolean {
        const result = this.getSharedDataState("_featureFlags", featureFlagName, defaultValue);
        if (result === undefined) {
            const message = `Could not find feature flag state for '${featureFlagName}'. Ensure that the feature flag is included in shared data.`;
            if ($DEBUG) {
                throw new Error(message);
            } else {
                console.warn(message);
            }

            return false;
        }

        return result;
    }

    private getSharedDataState(sharedDataKey: string, featureId: string, defaultValue: boolean | undefined): boolean | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const features = contributionService.getSharedData<IFeatureMap>(sharedDataKey);
        if (features) {
            const enabled = features[featureId];
            if (enabled !== undefined) {
                return enabled;
            }
        }

        return defaultValue;
    }
}

Services.add("IVssFeatureService", { serviceFactory: VssFeatureService });

/**
 * Gets the enabled state of a feature that was sent to the client using IClientFeatureProviderService (within a data provider or request handler).
 *
 * @param pageContext
 * @param featureId The full contribution id of the feature
 * @param defaultValue If specified, the value to return if the feature state was not seeded through shared data.
 */
export function isFeatureEnabled(pageContext: IVssPageContext, featureId: string, defaultValue?: boolean): boolean {
    const featureService = pageContext.getService<IVssFeatureService>("IVssFeatureService");
    return featureService.isFeatureEnabled(featureId, defaultValue);
}

/**
 * Gets the enabled state of a feature flag that was sent to the client using IClientFeatureProviderService (within a data provider or request handler).
 *
 * @param pageContext
 * @param featureFlagName The name of the feature flag
 * @param defaultValue If specified, the value to return if the feature flag was not seeded through shared data.
 */
export function isFeatureFlagEnabled(pageContext: IVssPageContext, featureFlagName: string, defaultValue?: boolean): boolean {
    const featureService = pageContext.getService<IVssFeatureService>("IVssFeatureService");
    return featureService.isFeatureFlagEnabled(featureFlagName, defaultValue);
}
